/**
 * מע"מ ומסים — "כמה מע"מ אני משלם בתקופה הזאת, מתי, ומה עוד המדינה מחכה לו" (spec-business §4.4).
 *
 * A: בורר תקופה דו-חודשית + "ייצוא לרו"ח" · B: חישוב התקופה (5) · לוח מועדים (4) · הוצאות ללא
 * חשבונית (3) · C: C7 מע"מ עסקאות מול תשומות (7) · היסטוריית דיווחים (5).
 */

import { useState } from 'react';
import type { SummaryResponse, VatResponse } from '../../../api-types.ts';
import { fmtDate, fmtMoney, fmtShortDate } from '../format.ts';
import { VAT_STATUS, he } from '../i18n/he.ts';
import { Icon } from '../components/Icon.tsx';
import { Card, CardHead, ChipScroller, Chip, EmptyState, FilterChip, Money, Num, Row, Skeleton } from '../components/ui.tsx';
import { ColumnChart } from '../charts/ColumnChart.tsx';
import { useApp } from '../hooks/useApp.tsx';
import { cacheKey } from '../hooks/cache.ts';
import { useQuery } from '../hooks/useQuery.ts';
import { Link, useRouter } from '../hooks/useRouter.tsx';
import { buildPath } from '../router.ts';
import { errorMessage } from '../api-select.ts';

export function VatScreen({ summary }: { summary: SummaryResponse | undefined }) {
  const { api, toast, openMenu, openDialog, closeLayer } = useApp();
  const { route, navigate } = useRouter();
  const selected = route.query.period ?? 'current';
  const vat = useQuery<VatResponse>(cacheKey('vat', { period: selected }), () => api.vat(selected), { scopes: ['vat'] });
  const asOf = summary?.asOf ?? vat.data?.asOf ?? '';
  const data = vat.data;
  const period = data?.period;
  const calc = period?.calc;

  const markReported = () => {
    if (!period) return;
    let paidAt = asOf;
    let amount = String((calc?.netVatAgorot ?? 0) / 100);
    openDialog({
      title: he.vat.markTitle(period.label),
      content: (
        <>
          <div className="field">
            <label htmlFor="vat-date">{he.vat.markDate}</label>
            <input id="vat-date" className="input" type="date" defaultValue={asOf} onChange={(event) => (paidAt = event.currentTarget.value)} />
          </div>
          <div className="field">
            <label htmlFor="vat-amount">{he.vat.markAmount}</label>
            <input id="vat-amount" className="input" inputMode="numeric" defaultValue={amount} onChange={(event) => (amount = event.currentTarget.value)} />
          </div>
        </>
      ),
      actions: [
        {
          label: he.vat.markReported,
          tone: 'primary',
          onClick: () => {
            api.markVatPeriod(period.id, { paid: { at: paidAt, amountAgorot: Math.round(Number(amount) * 100) || 0 } }).then(
              () => toast(he.vat.marked),
              (error: unknown) => toast(errorMessage(error), { error: true }),
            );
          },
        },
        { label: he.app.cancel },
      ],
    });
  };

  const markHasInvoice = (txId: string) => {
    api.updateExpense(txId, { hasTaxInvoice: true }).then(
      (response) => toast(he.vat.markedInvoice(fmtMoney(response.vat.inputVatAgorot), fmtMoney(response.vat.deltaAgorot))),
      (error: unknown) => toast(errorMessage(error), { error: true }),
    );
  };

  if (vat.loading && !data) {
    return (
      <div className="grid vat">
        <Card span={12}>
          <Skeleton className="ch" />
        </Card>
      </div>
    );
  }

  if (!data?.configured || !period || !calc) {
    return (
      <div className="grid vat">
        <Card span={12}>
          <EmptyState icon="landmark" text={he.vat.notConfigured} action={<Link className="btn sm" to={buildPath('settings', 'tax')}>{he.vat.toSettings}</Link>} />
        </Card>
      </div>
    );
  }

  const missing = data.missingInvoices;
  const missingNet = missing.reduce((sum, x) => sum + x.netAgorot, 0);
  const missingVat = missing.reduce((sum, x) => sum + x.vatAgorot, 0);
  const later = data.deadlines.filter((e) => e.daysUntilDue > 60);
  const upcoming = data.deadlines.filter((e) => e.daysUntilDue <= 60);
  const nextDay = upcoming.length ? upcoming.map((e) => e.dueDate).sort()[0]! : null;
  const nextTotal = upcoming.filter((e) => e.dueDate === nextDay).reduce((sum, e) => sum + e.amountAgorot, 0);
  const trend = data.trend;

  return (
    <div className="grid vat">
      {/* ── בורר תקופה ─────────────────────────────────────────────── */}
      <Card span={12} ariaLabel={he.vat.periodGroup}>
        <div className="card-h chips-h">
          <ChipScroller label={he.vat.periodGroup}>
            {data.periods
              .slice()
              .reverse()
              .map((option) => (
                <FilterChip
                  key={option.id}
                  label={`${option.label}${option.partial ? ` ${he.vat.periodOpen}` : ''}`}
                  pressed={option.id === period.id}
                  onClick={() => navigate(buildPath('vat', null, { period: option.id }))}
                />
              ))}
          </ChipScroller>
          <div className="card-a">
            <button
              type="button"
              className="btn sm"
              aria-haspopup="menu"
              onClick={(event) =>
                openMenu(
                  event.currentTarget,
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        closeLayer();
                        toast(he.app.fullVersion);
                      }}
                    >
                      <Icon name="fileDown" className="sm" />
                      {he.vat.exportCsv}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        closeLayer();
                        toast(he.app.fullVersion);
                      }}
                    >
                      <Icon name="fileDown" className="sm" />
                      {he.vat.exportPdf}
                    </button>
                  </>,
                  { label: he.vat.export },
                )
              }
            >
              <Icon name="fileDown" />
              {he.vat.export}
              <Icon name="chevDown" className="sm" />
            </button>
          </div>
        </div>
      </Card>

      {/* ── חישוב התקופה ───────────────────────────────────────────── */}
      <Card span={5} labelledBy="h-vc">
        <CardHead
          titleId="h-vc"
          title={period.partial ? he.vat.partialTitle(`1–${summary?.period.dayOfMonth ?? ''}.9`) : period.label}
          sub={calc.basis === 'cash' ? he.vat.basisCashSub : he.vat.basisAccrualSub}
        />
        <div className="rows">
          <Row
            label={
              <>
                {he.vat.outputVat}{' '}
                <Link className="link-btn" to={buildPath('invoices', null, { from: period.from, to: period.to })} style={{ fontWeight: 500 }}>
                  {period.partial ? he.vat.outputCountPartial(calc.outputCount, `1–${summary?.period.dayOfMonth ?? ''}.9`) : he.vat.outputCount(calc.outputCount)}
                </Link>
              </>
            }
            value={<Money agorot={calc.outputVatAgorot} />}
          />
          <Row
            label={
              <>
                {he.vat.inputVat}{' '}
                <Link className="link-btn" to={buildPath('expenses', null, { period: period.from.slice(0, 7) })} style={{ fontWeight: 500 }}>
                  {he.vat.inputCount(calc.inputCount)}
                </Link>
              </>
            }
            value={<Money agorot={-calc.inputVatAgorot} />}
          />
          <div className="note" style={{ padding: '2px 0 6px' }}>
            {he.vat.adjustments}
          </div>
          <Row
            total
            label={period.status === 'paid' ? he.vat.paid : period.partial ? he.vat.netVatPartial : he.vat.netVat}
            value={
              /* the screen's ONE hero figure (REDESIGN §8): what this period actually costs */
              <span className="vat-net">
                <Money agorot={calc.netVatAgorot} />
              </span>
            }
          />
        </div>
        <div className="inline">
          {period.status === 'paid' ? (
            <Chip tone="good" icon="check">
              {he.home.reportedPaid(fmtDate(period.paidAt))}
            </Chip>
          ) : period.partial ? (
            <>
              <Chip tone="info" icon="hourglass">
                {he.vat.periodOpenChip}
              </Chip>
              <span className="note">{he.vat.dueLineShort(fmtDate(period.dueDate))}</span>
            </>
          ) : (
            <>
              <Chip tone="warn" icon="clock">
                {he.vat.notReported}
              </Chip>
              <Chip tone={period.daysUntilDue <= 3 ? 'critical' : 'warn'} icon="calendar">
                {he.vat.dueLine(fmtDate(period.dueDate), period.daysUntilDue)}
              </Chip>
            </>
          )}
        </div>
        {calc.unpaidCount ? <div className="row muted" style={{ border: 0 }}>{he.vat.unpaidNote(calc.unpaidCount, fmtMoney(calc.unpaidOutputVatAgorot))}</div> : null}
        {period.status === 'paid' || period.partial ? null : (
          <div>
            <button type="button" className="btn primary" onClick={markReported}>
              <Icon name="check" />
              {he.vat.markReported}
            </button>
          </div>
        )}
      </Card>

      {/* ── לוח מועדים ─────────────────────────────────────────────── */}
      <Card span={4} labelledBy="h-vd">
        <CardHead titleId="h-vd" title={he.vat.calendar} sub={he.vat.calendarSub} />
        <div className="rows">
          {upcoming.length ? (
            upcoming.map((event) => {
              const tone = event.daysUntilDue <= 3 ? 'now' : event.daysUntilDue <= 10 ? 'soon' : '';
              return (
                <div className={`cal-item ${tone}`} key={event.id}>
                  <span className="cd">
                    {fmtShortDate(event.dueDate, asOf)}
                    <small>{event.daysUntilDue === 0 ? he.vat.today : he.vat.inDays(event.daysUntilDue)}</small>
                  </span>
                  <span className="cb">
                    <span>
                      <Icon name={event.type === 'vat' ? 'landmark' : event.type === 'national_insurance' ? 'shield' : 'percent'} className="xs" /> {event.label}
                    </span>
                    <small>{event.basisLabel}</small>
                  </span>
                  <span className="cv">
                    {event.estimated ? '~' : ''}
                    <Money agorot={event.amountAgorot} />
                  </span>
                </div>
              );
            })
          ) : (
            <div className="note">{he.vat.noDeadlines}</div>
          )}
          {nextDay ? <Row total label={he.vat.byDate(fmtShortDate(nextDay, asOf))} value={<Money agorot={nextTotal} />} /> : null}
          {later.length ? (
            <Row
              muted
              label={
                <>
                  {he.vat.laterPrefix}{' '}
                  {later.map((event) => `${fmtShortDate(event.dueDate, asOf)} ${event.label} ${event.estimated ? '~' : ''}${fmtMoney(event.amountAgorot)}`).join(' · ')}
                </>
              }
            />
          ) : null}
        </div>
      </Card>

      {/* ── הוצאות ללא חשבונית ─────────────────────────────────────── */}
      <Card span={3} labelledBy="h-vn">
        <CardHead titleId="h-vn" title={he.vat.missing} sub={he.vat.missingSub(period.label)} />
        {missing.length ? (
          <>
            <div className="inline">
              <Chip tone="warn" icon="flag">
                {he.vat.missingCount(missing.length)}
              </Chip>
              <span className="note">{he.vat.missingSummary(fmtMoney(missingNet), fmtMoney(missingVat))}</span>
            </div>
            <div className="rows">
              {missing.map((row) => (
                <div className="row" style={{ alignItems: 'flex-start' }} key={row.txId}>
                  <span className="lbl" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
                    <span style={{ color: 'var(--text-1)' }}>{row.merchant}</span>
                    <span className="note">
                      {fmtShortDate(row.day, asOf)} · {fmtMoney(row.netAgorot)} · {he.vat.inputVat} {fmtMoney(row.vatAgorot)}
                    </span>
                  </span>
                  <button type="button" className="btn xs" onClick={() => markHasInvoice(row.txId)}>
                    {he.vat.hasInvoice}
                  </button>
                </div>
              ))}
            </div>
            <div className="note">{he.vat.foreignNote}</div>
          </>
        ) : (
          <EmptyState text={he.vat.missingEmpty} good />
        )}
      </Card>

      {/* ── C7 ─────────────────────────────────────────────────────── */}
      <Card span={7} className="chart-c" labelledBy="h-c7">
        <CardHead titleId="h-c7" title={he.vat.trendQuestion} sub={he.vat.trendSub} />
        <ColumnChart
          labels={trend.map((p) => p.label)}
          mode="grouped"
          maxBar={30}
          height={220}
          series={[
            { key: 'output', label: he.vat.outputVat, series: 1, values: trend.map((p) => p.outputVatAgorot) },
            { key: 'input', label: he.vat.inputVat, series: 2, values: trend.map((p) => p.inputVatAgorot) },
          ]}
          topLabels={trend.map((p) => fmtMoney(p.netVatAgorot))}
          summary={`${he.vat.trendQuestion} ${trend.map((p) => `${p.label} ${fmtMoney(p.netVatAgorot)}`).join(', ')}`}
          tip={(index) => {
            const row = trend[index]!;
            return {
              title: row.label,
              rows: [
                { label: he.vat.colOutput, value: fmtMoney(row.outputVatAgorot), color: 'var(--series-1)', square: true },
                { label: he.vat.colInput, value: fmtMoney(row.inputVatAgorot), color: 'var(--series-2)', square: true },
                { label: he.vat.colNet, value: fmtMoney(row.netVatAgorot) },
                { label: he.vat.colStatus, value: VAT_STATUS[row.status] },
              ],
            };
          }}
          table={{
            caption: he.vat.trendSub,
            head: [he.vat.colPeriod, he.vat.colOutput, he.vat.colInput, he.vat.colNet, he.vat.colStatus],
            rows: trend.map((p) => [p.label, fmtMoney(p.outputVatAgorot), fmtMoney(p.inputVatAgorot), fmtMoney(p.netVatAgorot), VAT_STATUS[p.status]]),
          }}
        />
      </Card>

      {/* ── היסטוריית דיווחים ──────────────────────────────────────── */}
      <Card span={5} labelledBy="h-vh">
        <CardHead titleId="h-vh" title={he.vat.history} sub={he.vat.historySub} />
        <div className="tbl-wrap">
          <table className="tbl compact">
            <thead>
              <tr>
                <th scope="col">{he.vat.colPeriod}</th>
                <th scope="col" className="n">
                  {he.vat.colOutput}
                </th>
                <th scope="col" className="n">
                  {he.vat.colInput}
                </th>
                <th scope="col" className="n">
                  {he.vat.colNet}
                </th>
                <th scope="col">{he.vat.colPaidAt}</th>
                <th scope="col">{he.vat.colStatus}</th>
              </tr>
            </thead>
            <tbody>
              {data.history
                .slice()
                .reverse()
                .map((row) => (
                  <tr
                    key={row.id}
                    className="clickable"
                    tabIndex={0}
                    onClick={() => navigate(buildPath('vat', null, { period: row.id }))}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(buildPath('vat', null, { period: row.id }));
                      }
                    }}
                  >
                    <td>{row.label}</td>
                    <td className="n">
                      <Money agorot={row.calc.outputVatAgorot} />
                    </td>
                    <td className="n">
                      <Money agorot={row.calc.inputVatAgorot} />
                    </td>
                    <td className="n">
                      <b>
                        <Money agorot={row.calc.netVatAgorot} />
                      </b>
                    </td>
                    <td>{row.paidAt ? <Num>{fmtDate(row.paidAt)}</Num> : <span className="note">{he.app.none}</span>}</td>
                    <td>
                      {row.status === 'paid' ? (
                        <Chip tone="good" icon="check" small>
                          {he.vat.paid}
                        </Chip>
                      ) : row.partial ? (
                        <Chip tone="info" icon="hourglass" small>
                          {he.vat.partialChip}
                        </Chip>
                      ) : (
                        <Chip tone="warn" icon="clock" small>
                          {he.vat.notReported} · {he.vat.byDate(fmtShortDate(row.dueDate, asOf))}
                        </Chip>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
